/**
 * Marketing-campaign attribution (Zoho Campaigns and any other utm-tagged link).
 *
 * One path: a visit with `utm_campaign` is logged once and remembered for 90
 * days (last campaign clicked wins); after signup the remembered campaign is
 * stamped onto the new company once by `claim_campaign_attribution`. Separate
 * from Partner RevShare referral credit, which pays partners — campaigns never do.
 */
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'cannafax_campaign';
const FP_KEY = 'cannafax_visitor_fp';
const TTL_DAYS = 90;

export interface StoredCampaign { campaign: string; source: string | null; medium: string | null; content: string | null; at: number }

export function parseCampaignParams(search: string): Omit<StoredCampaign, 'at'> | null {
  const p = new URLSearchParams(search);
  const campaign = p.get('utm_campaign')?.trim();
  if (!campaign) return null;
  return { campaign, source: p.get('utm_source'), medium: p.get('utm_medium'), content: p.get('utm_content') };
}

export function readStoredCampaign(now = Date.now()): StoredCampaign | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as StoredCampaign | null;
    if (!parsed?.campaign || !parsed.at) return null;
    if ((now - parsed.at) / 86_400_000 > TTL_DAYS) { localStorage.removeItem(STORAGE_KEY); return null; }
    return parsed;
  } catch { return null; }
}

function fingerprint(): string {
  try {
    let fp = localStorage.getItem(FP_KEY);
    if (!fp) { fp = crypto.randomUUID(); localStorage.setItem(FP_KEY, fp); }
    return fp;
  } catch { return ''; }
}

/**
 * Logs and remembers a tagged campaign visit. Runs on page load, and again
 * when a short link (/s/:code) forwards in-app to its tagged destination.
 */
export function captureCampaignVisit(search = window.location.search, pathname = window.location.pathname) {
  const tag = parseCampaignParams(search);
  if (!tag) return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...tag, at: Date.now() })); } catch { /* best effort */ }
  void supabase.rpc('log_campaign_visit' as any, {
    _campaign: tag.campaign, _source: tag.source, _medium: tag.medium, _content: tag.content,
    _landing_path: pathname, _fingerprint: fingerprint(), _user_agent: navigator.userAgent,
  });
}

/** Short link for a campaign URL (same shortener as menu shares); falls back to the full link. */
export async function shortenCampaignUrl(url: string): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('shorten-url', { body: { url } });
    if (!error && typeof data?.shortUrl === 'string') return data.shortUrl;
  } catch { /* full link still works */ }
  return url;
}

/** Call once a user is signed in: credits the remembered campaign to their new company. */
export async function claimStoredCampaign() {
  const stored = readStoredCampaign();
  if (!stored) return;
  const { data, error } = await supabase.rpc('claim_campaign_attribution' as any, {
    _campaign: stored.campaign, _source: stored.source, _medium: stored.medium, _content: stored.content,
  });
  const reason = (data as { ok?: boolean; reason?: string } | null)?.reason;
  if (!error && reason !== 'no_company') {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
  }
}

/** Preset landing pages. Signed-out visitors to signed-in pages sign in first, then land there. */
export const CAMPAIGN_DESTINATIONS = [
  { key: 'home', label: 'Home page', path: '/', audience: 'New prospects' },
  { key: 'signup', label: 'Sign up', path: '/auth', audience: 'New prospects' },
  { key: 'desk', label: 'Your Desk', path: '/desk', audience: 'Existing users' },
  { key: 'buy-requests', label: 'Buy Requests', path: '/buy-requests', audience: 'Everyone' },
  { key: 'new-listing', label: 'Add a listing', path: '/listings/new', audience: 'Existing sellers' },
  { key: 'listings', label: 'My Inventory', path: '/my-listings', audience: 'Existing sellers' },
  { key: 'menus', label: 'My Menus', path: '/my-menu', audience: 'Existing sellers' },
] as const;
export type CampaignDestination = (typeof CAMPAIGN_DESTINATIONS)[number]['key'];

/** Normalise a custom path to a same-site path, or null if it is not one. */
export function normalizeCampaignPath(raw: string): string | null {
  const v = raw.trim();
  if (!v.startsWith('/') || v.startsWith('//') || /\s/.test(v)) return null;
  return v;
}

/** Where the link is shared; sets utm_source/utm_medium. */
export const CAMPAIGN_CHANNELS = [
  { key: 'zoho', label: 'Zoho email', source: 'zoho', medium: 'email' },
  { key: 'email', label: 'Other email tool', source: 'email', medium: 'email' },
  { key: 'sms', label: 'Text message', source: 'sms', medium: 'sms' },
  { key: 'linkedin', label: 'LinkedIn', source: 'linkedin', medium: 'social' },
  { key: 'social', label: 'Other social', source: 'social', medium: 'social' },
  { key: 'qr', label: 'QR code / print', source: 'qr', medium: 'offline' },
  { key: 'direct', label: 'Direct message', source: 'direct', medium: 'direct' },
] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number]['key'];

/** Build a tagged link for any channel (defaults to Zoho email). */
export function buildCampaignUrl(origin: string, campaign: string, destination: CampaignDestination | { path: string }, content?: string, channel: CampaignChannel = 'zoho') {
  const slug = campaign.trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, '-').replace(/^-+|-+$/g, '');
  const path = typeof destination === 'string'
    ? CAMPAIGN_DESTINATIONS.find((d) => d.key === destination)!.path
    : normalizeCampaignPath(destination.path) ?? '/';
  const ch = CAMPAIGN_CHANNELS.find((c) => c.key === channel) ?? CAMPAIGN_CHANNELS[0];
  const url = new URL(path, origin);
  url.searchParams.set('utm_source', ch.source);
  url.searchParams.set('utm_medium', ch.medium);
  url.searchParams.set('utm_campaign', slug);
  const c = content?.trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, '-');
  if (c) url.searchParams.set('utm_content', c);
  return url.toString();
}
